import { Form } from 'antd'
import { useCallback, useRef, useState } from 'react'
import * as rrweb from 'rrweb'
import { createContainer } from 'unstated-next'
import * as Sentry from '@sentry/react'
import { ERROR_TYPE_ENUM } from '@/services/sentry/constants'
import { newCustomError } from '@/services/sentry/errorWrapper'
import type { IFeedbackProps } from './type'
import sentryCollection from './utils/sentry'

export type ITypes = 'bug' | 'feature'

export const CHECKBOX_KEY = 'feedback_policy_checkbox'

export const FEEDBACK_CONFIG_KEY = 'os-feedback-config'

interface IStore {
  /**是否显示过导航 */
  guideConfig?: Record<string, any>
  /** 是否默认选中隐私协议 */
  checkbox?: number

  /** tool 位置 */
  left?: number
  top?: number
}

interface ITime {
  record_start_time?: string | number
  record_end_time?: string | number
}

// 10分钟
// const MAX_TIME = 60 * 1000 * 10

export const storeUtil = {
  get(): IStore {
    const data = localStorage.getItem(FEEDBACK_CONFIG_KEY)

    try {
      const realData = data ? JSON.parse(data) : {}

      return realData
    } catch (error) {
      return {}
    }
  },
  set(input: Partial<IStore>) {
    const data = this.get()

    localStorage.setItem(FEEDBACK_CONFIG_KEY, JSON.stringify(Object.assign(data, input)))

    return true
  },
}

function useContext() {
  const [feedbackVisible, setFeedbackVisible] = useState(true)
  const [lang, setLang] = useState<string>('')
  const [startFlag, setStartFlag] = useState(false)
  const config = useRef<IFeedbackProps>()

  const eventsRef = useRef<any[]>([])
  const stopFnRef = useRef<any>()
  const [visible, setVisible] = useState(false)
  const [recordToolVisible, setRecordToolVisible] = useState(false)
  const [events, setEvents] = useState<any[]>([])
  const [type, setType] = useState<ITypes | ''>()
  const [time, setTime] = useState<ITime>()

  const [showGuide, setShowGuide] = useState<boolean>(false)

  const cancelFnRef = useRef<any>()

  const [form] = Form.useForm()

  const updateTime = useCallback((data?: ITime) => {
    if (!data) {
      setTime(null)
    } else {
      setTime((old) => ({
        ...old,
        ...data,
      }))
    }
  }, [])

  const startRecord = useCallback(() => {
    // eslint-disable-next-line prefer-const
    let stopFn: any
    eventsRef.current = []

    // eslint-disable-next-line prefer-const
    // let timer: any

    const cancelBase = () => {
      if (typeof stopFn === 'function') {
        try {
          stopFn()
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error)
          Sentry.captureException(newCustomError(`rrweb cancel error: ${error?.toString()}`), (scope) => {
            scope.setTag('type', ERROR_TYPE_ENUM.JS)
            return scope
          })
        }
      }
      setStartFlag(false)
      setRecordToolVisible(false)
      setVisible(true)
      setFeedbackVisible(true)
      eventsRef.current = []
      stopFnRef.current = null
      cancelFnRef.current = null
      // clearTimeout(timer)
    }

    const stop = async () => {
      setEvents(eventsRef.current)
      cancelBase()
      updateTime({
        record_end_time: Date.now(),
      })
    }

    const cancel = async () => {
      cancelBase()
      updateTime()
    }

    setStartFlag(true)
    setFeedbackVisible(false)
    stopFnRef.current = stop
    cancelFnRef.current = cancel

    stopFn = rrweb.record({
      emit(event) {
        eventsRef.current.push(event)
      },
      sampling: {
        input: 'last', // 连续输入时，只录制最终值
        scroll: 150, // 每 150ms 最多触发一次
      },
      // packFn: rrweb.pack,
      plugins: [
        rrweb.getRecordConsolePlugin({
          level: ['error'],
          logger: window.console,
        }),
      ],
    })

    updateTime({
      record_start_time: Date.now(),
    })

    // 尝试打开
    try {
      const guideConfig = storeUtil.get().guideConfig || {}
      const uid = config?.current?.uid || ''
      if (uid && !guideConfig?.[uid]) {
        setShowGuide(true)
      }
    } catch (error) {
      setShowGuide(true)
    }

    // timer = setTimeout(() => {
    //   setEvents(eventsRef.current)
    //   stop?.()
    //   // 最长10分钟
    // }, MAX_TIME)
  }, [updateTime])

  const reset = useCallback(() => {
    form.setFieldsValue({
      email: undefined,
      description: undefined,
    })
    setType('')
    setEvents([])
    setRecordToolVisible(false)
    setVisible(false)
    eventsRef.current = []
    sentryCollection.clear()
    updateTime()
  }, [form, updateTime])

  const shutdownGuidTips = useCallback(() => {
    setShowGuide(false)
    const guideConfig = storeUtil.get().guideConfig || {}
    const uid = config?.current?.uid
    if (guideConfig && uid) {
      guideConfig[uid] = 1
      storeUtil.set({
        guideConfig,
      })
    }
  }, [setShowGuide])

  const showRecordTool = useCallback(() => {
    setVisible(false)
    setRecordToolVisible(true)
    cancelFnRef.current = () => {
      setStartFlag(false)
      setRecordToolVisible(false)
      setVisible(true)
      setFeedbackVisible(true)
    }
  }, [])

  return {
    startRecord,
    type,
    setType,
    form,
    events,
    setEvents,
    visible,
    setVisible,
    startFlag,
    setStartFlag,
    stopFnRef,
    cancelFnRef,
    recordToolVisible,
    setRecordToolVisible,
    feedbackVisible,
    setFeedbackVisible,
    showRecordTool,
    config,
    reset,
    lang,
    setLang,
    showGuide,
    shutdownGuidTips,
    time,
    updateTime,
  }
}

export default createContainer(useContext)
